export const getLuminance = (hex: string) => {
	const r = parseInt(hex.slice(1, 3), 16) / 255
	const g = parseInt(hex.slice(3, 5), 16) / 255
	const b = parseInt(hex.slice(5, 7), 16) / 255
	return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export const isColourDark = (hex: string) => getLuminance(hex) < 0.5

export const getContrastingTextColor = (backgroundHex: string): string =>
	isColourDark(backgroundHex) ? '#ffffff' : '#000000'

// Above this perceived luminance, the color blends into the white page
// background and elements rendered with it (radio borders, question number
// badges, submit button) become invisible. Kept in sync with
// `BrandControlColor.util.ts` in form-viewer — same parsing rules (incl.
// 3-char hex expansion) and same Rec. 601 luminance coefficients so the
// in-editor preview's verdict matches what the public form viewer applies.
const TOO_LIGHT_THRESHOLD = 0.7

function getPerceivedLuminance(hexColor: string): number | null {
	let hex = hexColor.replace(/^#/, '')

	if (hex.length === 3) {
		hex = hex.split('').map(char => char + char).join('')
	}

	if (hex.length !== 6 || /[^0-9a-f]/i.test(hex)) {
		return null
	}

	const r = parseInt(hex.slice(0, 2), 16)
	const g = parseInt(hex.slice(2, 4), 16)
	const b = parseInt(hex.slice(4, 6), 16)

	return (0.299 * r + 0.587 * g + 0.114 * b) / 255
}

export const isColourTooLightOnWhite = (hex: string | undefined | null): boolean => {
	if (!hex) {
		return false
	}
	const luminance = getPerceivedLuminance(hex)
	return luminance !== null && luminance > TOO_LIGHT_THRESHOLD
}

// Picks the color used as a solid fill / border for branded controls. When
// the user-chosen brand background lacks contrast against the white preview
// canvas (e.g. white or pale yellow), fall back to the user's foreground so
// controls remain visible while still honouring their palette.
export const chooseBrandControlColor = (
	brandBackground: string | undefined | null,
	brandForeground: string | undefined | null,
): string => {
	if (brandBackground && !isColourTooLightOnWhite(brandBackground)) {
		return brandBackground
	}
	if (brandForeground && !isColourTooLightOnWhite(brandForeground)) {
		return brandForeground
	}
	return '#000000'
}
